import { generateText, type JSONContent, type TextSerializer } from "@tiptap/core";
import { generateJSON } from "@tiptap/html";

import { rehydrateSanitizedHtmlForEditor } from "@av/components/atoms/html-transport";
import { safeJSONparse } from "@av/utils/safe-json-parse";
import { richTextEditorExtensions } from "./editor-extensions";

export type EditorContentType = "plainText" | "richText";
export type ParsedRichTextValue = string | object;
export type EditorMeta = { plainText: string; isEmpty: boolean };

function normalizeLineEndings(value = "") {
  return value.replace(/\r\n?/g, "\n");
}

export function parseEditorRichTextValue(value = ""): ParsedRichTextValue {
  const parsed = safeJSONparse(value);

  if (!parsed) {
    return value;
  }

  if (Array.isArray(parsed)) {
    return { type: "doc", content: parsed };
  }

  return parsed;
}

function stripTrailingLineBreaks(value: string) {
  return value.replace(/\n+$/g, "");
}

const textSerializers: Record<string, TextSerializer> = {
  hardBreak: ({ index, parent }) => (index === parent.childCount - 1 ? "" : "\n"),
};

function toTiptapDoc(content: unknown): JSONContent {
  if (Array.isArray(content)) {
    return { type: "doc", content: content as JSONContent[] };
  }

  return content as JSONContent;
}

function getPlainTextFromTiptapNode(content: unknown): string {
  return normalizeLineEndings(
    stripTrailingLineBreaks(
      generateText(toTiptapDoc(content), richTextEditorExtensions, {
        blockSeparator: "\n",
        textSerializers,
      })
    )
  );
}

function getPlainTextFromBrowserHtml(value: string) {
  return getPlainTextFromTiptapNode(generateJSON(value, richTextEditorExtensions));
}

/**
 * Convert HTML (e.g. a generated draft) into the tiptapJson wire format the
 * editor stores in form fields: `JSON.stringify` of the doc's `content` array,
 * matching what Editor.tsx's onUpdate emits, so `setFieldValue` writes land in
 * the same shape as a normal edit.
 */
export function htmlToRichTextEditorValue(html: string): string {
  const doc = generateJSON(html, richTextEditorExtensions);

  return JSON.stringify(doc.content ?? []);
}

export function getDocPlainTextLength(doc: { toJSON: () => unknown }) {
  return getPlainTextFromTiptapNode(doc.toJSON()).length;
}

export function getEditorPlainText(value = "", type: EditorContentType = "richText") {
  if (type === "plainText") {
    return normalizeLineEndings(value);
  }

  const content = safeJSONparse(value);

  if (!content) {
    if (typeof document === "undefined") {
      return normalizeLineEndings(stripTrailingLineBreaks(value.replace(/<[^>]*>/g, "")));
    }

    return getPlainTextFromBrowserHtml(value);
  }

  return getPlainTextFromTiptapNode(content);
}

export function isEditorEmpty(value = "", type: EditorContentType = "richText") {
  return getEditorPlainText(value, type).trim().length === 0;
}

export function stripTrailingEmptyParagraphs(html: string) {
  return html.replace(/(?:<p>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>\s*)+$/giu, "");
}

export function transformPastedHtmlForEditor(html: string) {
  return rehydrateSanitizedHtmlForEditor(html);
}

export function stripTrailingHardBreak(html: string) {
  return html.replace(/(?:<br\s*\/?>\s*)+(<\/p>\s*)$/iu, "$1");
}

export function stripBoundaryEmptyParagraphs(html: string) {
  const withoutBoundaryParagraphs = stripTrailingEmptyParagraphs(html).replace(
    /^(?:\s*<p>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>)+/giu,
    ""
  );

  return stripTrailingHardBreak(withoutBoundaryParagraphs);
}
